import React, { useMemo, useState } from "react";
import { AttachmentView } from "lib/attachmentViews";
import CachedImage from "components/CachedImage";
import ImageGallery from "components/ImageGallery";
import MediaPreviewModal from "components/MediaPreviewModal";
import MessageAttachView from "components/MessageAttachView";
import "./style.scss";

interface Props {
  attachments: AttachmentView[];
}

/**
 * Renders all attachments of a single message:
 *  - images  → album grid (layout adapts to count); tap opens a swipeable gallery
 *  - others  → file cards (video / audio / doc); tap opens MediaPreviewModal
 *
 * Owns the gallery + preview modal state so the host bubble stays simple.
 * Shared by MessageBubble (DM/group) and MessageDefault (livechat).
 */
function MessageAttachments({ attachments }: Props) {
  // Only images that actually have a thumbnail go into the grid; anything
  // else (incl. an image whose thumbnail failed to resolve) falls back to a card.
  const images = useMemo(() => attachments.filter((a) => a.isImage && a.src), [attachments]);
  const others = useMemo(() => attachments.filter((a) => !(a.isImage && a.src)), [attachments]);

  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);
  const [previewFile, setPreviewFile] = useState<AttachmentView | null>(null);

  const galleryImages = useMemo(
    () =>
      images.map((img) => ({
        src: img.src as string, // 720p thumbnail — instant placeholder
        url: img.url, // full-res original — for crisp zoom + download
        alt: img.fileName,
        fileName: img.fileName,
        mediaType: img.mediaType,
      })),
    [images]
  );

  if (attachments.length === 0) return null;

  // count bucket caps at 5 ("5+"); layout rules live in style.scss
  const gridClass = `message-attachments__grid __count-${Math.min(images.length, 5)}`;

  return (
    <div className="message-attachments">
      {images.length > 0 && (
        <div className={gridClass}>
          {images.map((img, i) => (
            <div key={img.id} className="message-attachments__tile" onClick={() => setGalleryIndex(i)}>
              {/* width/height hint the browser-computed layout slot so the
                  tile reserves space BEFORE CachedImage's async fetch
                  resolves — without this the tile collapses to 0 height
                  and snaps on load, yanking the chat viewport. The CSS
                  `min-height: 200px` on the single-image tile provides
                  the matching vertical floor (multi-image grids already
                  reserve via `aspect-ratio: 1`). */}
              <CachedImage src={img.src as string} alt={img.fileName} width={300} height={200} />
            </div>
          ))}
        </div>
      )}

      {others.map((file) => (
        <div key={file.id} className="message-attachments__file" onClick={() => setPreviewFile(file)}>
          <MessageAttachView attachedFileData={file} link={undefined} showPreview={true} />
        </div>
      ))}

      {galleryIndex !== null && (
        <ImageGallery images={galleryImages} startIndex={galleryIndex} onClose={() => setGalleryIndex(null)} />
      )}

      {previewFile && (
        <MediaPreviewModal
          src={previewFile.url}
          fileName={previewFile.fileName}
          mediaType={previewFile.mediaType}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>
  );
}

export default React.memo(MessageAttachments);
